import type { Doc } from "building-types/doc";
import type { ShapeHandle } from "building-types/handles/docEntityHandleTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { annotationShapeToPolyLine2 } from "./annotationShapeUtils";
import {
  getFloorIdForSplitLine,
  getFloorSplitData,
  isHorizontalFloorAssemblyFreeForm,
  isSolidFloorAssemblyOverride,
} from "./elements/floor/floorAssembly";
import { floorOpeningIndexFromShapeId, floorPerimeterIndexFromShapeId } from "./elements/floor/floorShapeId";
import { docGetTableEntity } from "./entity/docGetTableEntity";
import { isEditableCeilingOutline } from "./isEditableCeilingOutline";
import { isVirtualRoofBlock } from "./roofSystemTools";

/**
 * This function gets a PolyLine2 from a ShapeHandle. It reads essentially directly from the Doc. So,
 * this function does not apply any modifications to the shape like edge hosted transforms or modifiers
 * which are computed during building generation. Use buildingGetShapeFallbackToDoc for transformed geometry.
 */

export function docGetShape(doc: Doc, { parent, shapeId }: ShapeHandle): PolyLine2 | null {
  switch (parent.type) {
    case "ANNOTATION": {
      const annotation = docGetTableEntity(doc, parent);

      if (!annotation) {
        return null;
      }

      return annotationShapeToPolyLine2(annotation, doc.assets) ?? null;
    }
    case "MEASUREMENT": {
      const measurement = docGetTableEntity(doc, parent);
      if (!measurement) {
        return null;
      }

      return measurement.shapes[shapeId]?.path;
    }
    case "STAIR_BLOCK":
    case "VIEWPORT":
    case "ROOM": {
      const room = docGetTableEntity(doc, parent);
      if (!room || shapeId !== "outline") {
        return null;
      }

      return room.outline;
    }
    case "ROOF": {
      const roof = docGetTableEntity(doc, parent);
      if (!roof || !isVirtualRoofBlock(roof.blocks)) {
        return null;
      }

      return roof.blocks.perimeter;
    }
    case "SITE_SHAPE": {
      const shape = docGetTableEntity(doc, parent);
      if (!shape) {
        return null;
      }

      return shape.geom;
    }
    case "REFERENCE_GEOM": {
      const geom = docGetTableEntity(doc, parent);
      if (!geom || shapeId !== "geom") {
        return null;
      }

      return geom.geom;
    }
    case "RAILING": {
      const railing = docGetTableEntity(doc, parent);

      if (!railing || shapeId !== "path") {
        return null;
      }

      return railing.path;
    }
    case "FLOOR_FINISH": {
      const floorFinish = docGetTableEntity(doc, parent);

      if (!floorFinish || shapeId !== "finishPerimeter") {
        return null;
      }

      return floorFinish.finishPerimeter ?? null;
    }
    case "CEILING": {
      const ceiling = docGetTableEntity(doc, parent);

      if (!ceiling || shapeId !== "outline") {
        return null;
      }

      const { isEditable, editableOutline } = isEditableCeilingOutline(ceiling);
      if (!isEditable) {
        return null;
      }

      return editableOutline;
    }
    case "ROOM_LAYOUT_INSTANCE": {
      return null;
    }
    case "EDIT_MODE": {
      // placeholder
      return polyLine2New([]);
    }
    case "FLOOR": {
      const floor = docGetTableEntity(doc, parent);
      if (!floor || !isSolidFloorAssemblyOverride(floor) || !isHorizontalFloorAssemblyFreeForm(floor)) {
        return null;
      }
      const perimeterIndex = floorPerimeterIndexFromShapeId(shapeId);
      if (perimeterIndex !== null) {
        return floor.perimeters?.[perimeterIndex] ?? null;
      }
      const openingIndex = floorOpeningIndexFromShapeId(shapeId);
      if (openingIndex !== null) {
        return floor.openings?.[openingIndex] ?? null;
      }
      return null;
    }
    case "FOUNDATION": {
      const foundation = docGetTableEntity(doc, parent);
      if (!foundation || shapeId !== "outline") {
        return null;
      }
      return foundation.perimeter ?? polyLine2New([]);
    }
    case "FLOOR_BAY":
      return null;
    case "FLOOR_SPLIT_LINE": {
      if (shapeId !== "splitLine") {
        return null;
      }
      const slFloorId = getFloorIdForSplitLine(doc.floors, parent.id);
      const slFloor = slFloorId ? doc.floors?.[slFloorId] : undefined;
      if (!slFloor) {
        return null;
      }
      const slSplitData = getFloorSplitData(slFloor);
      return slSplitData?.splitLines?.[parent.id]?.polyline ?? null;
    }
    default:
      assertUnreachable(parent);
  }
}
